import { Component, Inject, ViewChild, OnInit, HostListener } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatMenuTrigger } from '@angular/material/menu';
import { Overlay } from '@angular/cdk/overlay';
import { Clipboard } from '@angular/cdk/clipboard';
import { MediaService } from '@core/services/media.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject, catchError, filter, mergeMap, ObservableInput, Observable, of, from, switchMap } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { TranslateService } from '@ngx-translate/core';

import {
    DialogResponse,
    SfxDialog
} from '@shared/components/dialog/dialog.component';
import { MatDialog } from '@angular/material/dialog';

import { ChoosePipelineComponent, ChoosePipelineData } from './dialogs/choose-pipeline-content.component';

interface CreateResponse {
    data: {
        contact?: any,
        account?: any,
        addedToAccount?: any,
        opportunity?: number
    }
}

type PropValueUnion<T extends readonly any[], K extends keyof T[number]> = T[number][K];

interface LeadCreatePayload {
    // eslint-disable-next-line camelcase
    linkedin_id: string,
    type: string,
    account?: number
    pipeline?: number
}

@Component({
    selector: 'sfx-lead-search',
    templateUrl: './lead-search.component.html',
    styleUrls: ['./lead-search.component.scss']
})
export class LeadSearchComponent implements OnInit {
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
    @ViewChild('filtersSidenav') filtersSidenav!: MatSidenav;

    trigger$ = new Subject<any>();


    activeEmailMenuTrigger: MatMenuTrigger | null = null;
    activeCompanyMenuTrigger: MatMenuTrigger | null = null;

    isWalkthroughShowing = false;

    leads: any[] = [];
    numberOfLeads: number;
    pageSize = 20;
    currentPage = 0;
    createOptions: { name: string, display: any }[] = [];

    credits: number;
    findingEmails: string[] = [];
    attemptedEmailFind: string[] = [];
    currentEmail: string;

    filters: any;
    badFilterError = null;
    usedBadFilter = false;
    isLoading = true;
    createdFromFirstLeadFlag = false;
    creatingLeads: string[] = [];
    isLeadFilterApplied = () => false;

    isFindEmailEnabled = false;

    constructor(
        public media: MediaService,
        private matDialog: MatDialog,
        private clipboard: Clipboard,
        private overlay: Overlay,
        @Inject(DOCUMENT) private document: Document,
        @Inject('exceptionHandler') public exceptionHandler: any,
        @Inject('dialogService') private dialogService: any,
        @Inject('sfWalkthrough') private sfWalkthrough: any,
        @Inject('modelService') public modelService: any,
        @Inject('stateService') private stateService: any,
        @Inject('emailService') private emailService: any,
        @Inject('leadsService') private leadsService: any,
        @Inject('utilsService') public utilsService: any,
        @Inject('windowService') private windowService: any,
        @Inject('pipelinesService') private pipelinesService: any,
        @Inject('contactService') private contactService: any,
        @Inject('flagsService') private flagsService: any,
        @Inject('workflowService') private workflowService: any,
        @Inject('filterService') private filterService: any,
        private translate: TranslateService
    ) {
        this.isWalkthroughShowing = this.sfWalkthrough.isShowing;
    }

    ngOnInit(): void {

        const savedPageIndex = localStorage.getItem('paginatorPageIndex');
        const savedPageSize = localStorage.getItem('paginatorPageSize');

        if (savedPageIndex !== null && savedPageSize !== null) {
            this.currentPage = +savedPageIndex;
            this.pageSize = +savedPageSize;
        }
        else {
            this.currentPage = 0;
            this.pageSize = 20;
        }

        this.isLeadFilterApplied = this.filterService.isLeadFilterApplied;

        this.createdFromFirstLeadFlag = !!this.modelService.me.flags.find((flag: any) => flag.name === 'createdFromFirstLead')?.state;

        this.isFindEmailEnabled = this.flagsService.get('findEmail') === true;

        this.createOptions = [
            { name: 'contact', display: this.translate.instant('Contact') },
            { name: 'contact_account', display: this.translate.instant('Contact & Account') },
            { name: 'contact_account_opportunity', display: this.translate.instant('Contact, Account & Opportunity') }
        ];

        this.credits = this.modelService.me.team.credit_quota - this.modelService.me.team.credit_usage;

        this.filters = this.filterService.getFilter('lead', { raw: true });

        this.filterGet();
    }

    toggleFilterMenu() {
        this.filtersSidenav.toggle();
    }

    getDefaultFilters = () => {

        return this.filterService.getDefaultFilters('lead', this.modelService.me);
    }

    boundOnFilterApply = (event: any) => this.onFilterApply(event);

    onFilterApply(event: any) {

        this.isLoading = true;

        const currentPage = this.currentPage;

        this.currentPage = 0;

        localStorage.setItem('paginatorPageIndex', currentPage.toString());

        event.rules.find((rule: any) => rule.id === 'lead_people.job_title')?.raw_value?.forEach((val: any) => {
            if (val.createNew === true) {
                this.leadsService.createTeamJobTitle(val.name);
                delete val.createNew;
            }
        });

        this.filters = this.filterService.setFilter('lead', event.rules, event.saved_filter_id);

        if (this.badFilterError) {
            return;
        }

        if (currentPage === 0) {
            return this.filterGet();
        }

        return this.paginator.firstPage();
    }

    private filterGet() {

        const filterRules = this.filterService.getFilter('lead');

        filterRules.forEach((rule: any) => {

            if (rule.id === 'lead_people.location') {
                const rawRule = this.filterService.getFilter('lead', { raw: true })
                    .find((r: any) =>
                        r.id === 'lead_people.location'
                        && this.filterService.arraysAreEqualUnordered(rule.value, r.value)
                    );
                rule.raw_value = rawRule.raw_value;
            }
        });

        if (!filterRules?.length) {
            this.isLoading = false;
            this.leads = [];
            this.numberOfLeads = 0;

            return;
        }

        const requestOptions = {
            limit: this.pageSize,
            offset: this.currentPage * this.pageSize,
            filterRules
        };

        this.leadsService.filterGet(requestOptions).then((response: any) => {

            this.isLoading = false;

            if (response) {
                this.leads = response.data.leads;
                this.numberOfLeads = response.data.count;
            }
        }).catch(this.handleServerError);
    }

    handleServerError(response: any) {

        if (response.config.method === 'HEAD') {

            return;
        }

        this.isLoading = false;
        this.numberOfLeads = 0; // Makes sure the virtual repeat doesn't show anymore

        if (response && (response.status === 422 || response.status === 402)) {
            this.usedBadFilter = true;

            if (response.data.filter) {

                const selectedRules = this.filterService.flatten(this.filterService.getFilter('lead', { raw: true }));
                const hydratedRules = this.filterService.flatten(response.data.filter.rules);

                const rulesToApply = this.filterService.intersectAndEnrich(selectedRules, hydratedRules);

                this.filters = this.filterService.setFilter('lead', rulesToApply);

                this.trigger$.next({ action: 'setFilters', payload: this.filters });
            }
        }
    }

    handleFilterError($event: any) {

        if ($event.message === null && this.badFilterError) {

            this.stateService.reload();
        }

        if ($event.message) {
            this.usedBadFilter = true;
        }

        this.badFilterError = $event.message;
    }

    openMenuIfExists(lead: any, trigger: MatMenuTrigger, anchor: HTMLSpanElement) {
        const email = this.contactExistsAndHasEmail(lead);

        if (!email) {
            return;
        }

        this.activeEmailMenuTrigger = trigger;

        this.currentEmail = email;
        // eslint-disable-next-line angular/timeout-service
        setTimeout(() => trigger.openMenu(), 0);

        const sub = trigger.menuOpened.subscribe(() => {
            // Access the private properties after the overlay is created.
            // eslint-disable-next-line no-underscore-dangle
            const overlayRef = (trigger as any)._overlayRef;

            if (overlayRef && anchor) {
                const positionStrategy = this.overlay.position()
                    .flexibleConnectedTo(anchor)
                    .withPositions([{
                        originX: 'start',
                        originY: 'center',
                        overlayX: 'start',
                        overlayY: 'center'
                    }]);
                overlayRef.updatePositionStrategy(positionStrategy);
            }

            // Unsubscribe once done to avoid memory leaks.
            sub.unsubscribe();
        });
    }

    // Close menu on mouse leave
    closeEmailMenu() {
        this.activeEmailMenuTrigger?.closeMenu();
    }

    copyEmailToClipboard(lead: any) {

        this.clipboard.copy(lead.contacts[0].email);
    }

    hasCompanyMenu(lead: any): boolean {
        if (!lead.linked_account) {
            if (!lead.company.name && !lead.company.picture) {
                return false;
            }
        }

        return true;
    }

    openCompanyMenuIfExists(trigger: MatMenuTrigger, anchor: HTMLSpanElement) {

        if (!this.media.isActive('gt-sm')) {
            return;
        }

        if (this.activeCompanyMenuTrigger) {
            this.activeCompanyMenuTrigger.closeMenu();
        }

        trigger.openMenu();

        const sub = trigger.menuOpened.subscribe(() => {
            // eslint-disable-next-line no-underscore-dangle
            const overlayRef = (trigger as any)._overlayRef;

            if (overlayRef && anchor) {
                const positionStrategy = this.overlay.position()
                    .flexibleConnectedTo(anchor)
                    .withPositions([{
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'bottom'
                    }]);
                overlayRef.updatePositionStrategy(positionStrategy);
            }

            // Unsubscribe once done to avoid memory leaks.
            sub.unsubscribe();
        });

        this.activeCompanyMenuTrigger = trigger;
    }

    @HostListener('document:mousemove', ['$event'])
    onGlobalMouseMove(event: MouseEvent) {
        if (!this.activeCompanyMenuTrigger) {
            return;
        }

        const panelEl = this.document.querySelector<HTMLElement>('.company-menu-content');
        if (!panelEl) {
            return;
        }

        const { left, right, top, bottom } = panelEl.getBoundingClientRect();
        const { clientX: x, clientY: y } = event;

        // If the cursor is outside the panel → close
        if (x < left - 25 || x > right + 50 || y < top - 25 || y > bottom + 50) {
            this.activeCompanyMenuTrigger.closeMenu();
        }
    }

    closeCompanyMenu() {
        this.activeCompanyMenuTrigger?.closeMenu();
    }

    openWebsite(website: string | null | undefined, event: Event) {
        event.stopPropagation();

        const value = website?.trim();
        if (!value) {
            return;
        }

        const normalizedWebsite = /^https?:\/\//i.test(value)
            ? value
            : `https://${value}`;

        try {
            const url = new URL(normalizedWebsite);

            if (!['http:', 'https:'].includes(url.protocol)) {
                return;
            }

            this.windowService.open(url.toString(), '_blank', 'noopener');
        }
        catch {
            // Do not open malformed URLs.
        }
    }

    onContactClick(contactId: number) {
        this.stateService.go('contact', { id: contactId });
    }

    goToAccount(account: any, event: any) {

        // Open in new tab when clicking on the shortcut icon
        if (event?.target?.classList.contains('open-account-in-new-tab')) {
            const url = this.stateService.href('accounts.account.feed', { id: account.id });
            this.windowService.open(url, '_blank', 'noopener');
            return;
        }

        return this.stateService.go('accounts.account.feed', { id: account.id });
    }

    onPageChange(event: PageEvent) {
        this.pageSize = event.pageSize;
        this.currentPage = event.pageIndex;
        this.filterGet();

        localStorage.setItem('paginatorPageIndex', this.currentPage.toString());
        localStorage.setItem('paginatorPageSize', this.pageSize.toString());
    }

    isTrial(): boolean {

        if (this.modelService.me.team.subscribed) {
            return false;
        }

        if (this.modelService.me.trial_expiry_date && new Date(this.modelService.me.trial_expiry_date) > new Date()) {
            return true;
        }

        return false;
    }

    addContactToWorkflow(lead: any) {

        const contact = lead.contacts.find((c: any) => c.firstname && c.lastname && (c.domain || lead.company.domain || lead.company.website));
        return this.dialogService.show({
            controllerAs: 'vm',
            fullscreen: this.media.isActive('xs'),
            multiple: true,
            clickOutsideToClose: true,
            escapeToClose: true,
            templateUrl: 'app-ajs/components/workflows/workflow/addcontacttoworkflowdialog/addContactToWorkflowDialog.html',
            controller: 'AddContactToWorkflowDialogController',
            locals: {
                contactId: contact.id
            },
            bindToController: true
        }).then((result: any) => {

            if (result.workflow) {
                const addFilter = {
                    condition: 'AND',
                    rules: [{
                        id: 'contact.id',
                        value: [contact.id],
                        operator: 'equal'
                    }]
                };

                return this.workflowService.addToIndividualRecordFilter(result.workflow.id, addFilter).then(() => {

                    this.utilsService.showSuccessToast(this.translate.instant('Contact added to workflow.'));
                });
            }
        });
    }

    isFindEmailButtonDisabled(lead: any) {

        return !lead.contacts || !lead.contacts.some((c: any) => c.firstname && c.lastname && (lead.company?.domain || lead.company?.name));
    }

    isEmailButtonDisabled(lead: any): boolean {
        if (this.contactExistsAndHasEmail(lead)) {
            return false; // Email already exists, button is not disabled
        }

        const disabled = this.credits <= 0 || this.attemptedEmailFind.includes(lead.linkedin_id) || !this.isFindEmailEnabled || this.isFindEmailButtonDisabled(lead);

        return disabled;
    }

    onEmailButtonClick(lead: any, $event: any) {

        const email = this.contactExistsAndHasEmail(lead);
        if (email) {
            return this.emailService.mailto(email, $event);
        }
        else if (this.isEmailButtonDisabled(lead)) {
            return;
        }

        return this.findEmail(lead);
    }

    isCreatingLead(lead: any) {
        return this.creatingLeads.includes(lead.linkedin_id);
    }

    contactExistsAndHasEmail(lead: any) {

        const ret = lead.contacts && lead.contacts.find((c: any) => !!c.email)?.email;

        return ret;
    }

    findEmail(lead: any) {

        const contact = lead.contacts.find((c: any) => c.firstname && c.lastname && (lead.company.domain || lead.company.name));

        if (!contact) {
            return this.utilsService.showInfoToast(this.translate.instant('No contact with name and domain found. No credits used.'), null, 5000);
        }

        this.findingEmails.push(lead.linkedin_id);
        this.attemptedEmailFind.push(lead.linkedin_id);

        return this.contactService.findEmail({
            firstName: contact.firstname,
            lastName: contact.lastname,
            domain: lead.company.domain,
            company: lead.company.name
        }).then((response: any) => {

            const foundEmail = response.data.email;

            this.findingEmails = this.findingEmails.filter((id) => id !== lead.linkedin_id);

            if (!foundEmail) {
                return this.utilsService.showInfoToast(this.translate.instant('No quality email found. No credits used.'), null, 5000);
            }

            this.contactService.update({
                ...contact,
                email: foundEmail
            }).then((updateResponse: any) => {

                if (updateResponse.data.success || updateResponse.status === 200) {
                    for (const c of lead.contacts) {
                        if (c.id === contact.id) {
                            c.email = foundEmail;
                        }
                    }
                }
            });

            const creditsLeft = response.headers()['x-credits-remaining'];
            this.modelService.me.team.credit_usage = this.modelService.me.team.credit_quota - creditsLeft;

            let message = this.translate.instant('Email found.');

            if (this.modelService.me.team.plan !== 5) {
                message += (' ' + creditsLeft + '/' + this.modelService.me.team.credit_quota + ' ' + this.translate.instant('credits remaining.'));
            }

            this.credits = creditsLeft;

            this.utilsService.showSuccessToast(message);
        }).catch((err: any) => {

            if ((err?.status === 402 || err?.data?.statusCode === 402) && err?.data?.message) {
                this.utilsService.showInfoToast(err.data.message);
                this.credits = 0;
            }
            else if (err?.data?.message) {
                this.utilsService.showErrorToast(err.data.message);
            }

            this.findingEmails = this.findingEmails.filter((id) => id !== lead.linkedin_id);
        });
    }

    private createAction(lead: any, createOption: PropValueUnion<typeof this.createOptions, 'name'>): void {
        // Track that we're attempting an email lookup
        this.attemptedEmailFind.push(lead.linkedin_id);

        const payload: LeadCreatePayload = {
            linkedin_id: lead.linkedin_id,
            type: createOption
        };

        // Determine which sequence of dialogs or service call to run
        let action$: Observable<CreateResponse | null>;

        if (createOption === 'contact_account' || createOption === 'contact_account_opportunity') {
            // If there are existing matching accounts, let the user choose one
            if (lead.company.matching_accounts?.length) {
                action$ = this.chooseAccountDialog(lead, createOption);

            }
            else if (createOption === 'contact_account_opportunity') {
                // No accounts yet but opportunity requested: first pick a pipeline
                action$ = from(this.pipelinesService.get()).pipe(
                    switchMap((response) => this.choosePipelineDialog(payload, response))
                ) as Observable<CreateResponse>;

            }
            else {
                // No matching accounts and only contact+account requested: create directly
                this.creatingLeads.push(lead.linkedin_id);
                action$ = from(this.leadsService.createFromLead(payload)) as Observable<CreateResponse>;
            }

        }
        else {
            // Simple create (e.g. contact only)
            this.creatingLeads.push(lead.linkedin_id);
            action$ = from(this.leadsService.createFromLead(payload)) as Observable<CreateResponse>;
        }

        // Handle errors and subscription
        action$.pipe(
            catchError((err) => {
                this.leadCreationErrorHandler(err, lead);

                return of(null);
            })
        ).subscribe({
            next: (result) => {
                if (!result) {
                    return;
                }

                let toastText = this.translate.instant('Successfully created');

                if (result.data.contact) {
                    toastText += ' ' + this.translate.instant('a contact');
                }

                if (result.data.account && !result.data.opportunity) {
                    toastText += ' ' + this.translate.instant('and an account');
                }
                else if (result.data.account) {
                    toastText += ', ' + this.translate.instant('an account');
                }

                if (result.data.opportunity) {
                    toastText += ' ' + this.translate.instant('and an opportunity');
                }

                const creditsLeft = (result as any).headers()['x-credits-remaining'];
                this.modelService.me.team.credit_usage = this.modelService.me.team.credit_quota - creditsLeft;

                this.credits = creditsLeft;

                this.utilsService.showSuccessToast(toastText);

                // eslint-disable-next-line angular/timeout-service
                setTimeout(() => {
                    lead.contacts = [{ ...result.data.contact }];
                    if (result.data.account) {
                        this.leads.forEach((leadItem) => {
                            if (!leadItem.linked_account && this.isTheSameCompany(leadItem.company, lead.company)) {
                                leadItem.company.matching_accounts = [{ ...result.data.account }, ...(leadItem.company.matching_accounts ?? [])];
                            }
                        });

                        lead.linked_account = result.data.account.id;
                    }
                    else if (result.data.addedToAccount) {

                        lead.linked_account = result.data.addedToAccount.id;
                        lead.company.matching_accounts = [{ ...result.data.addedToAccount }];
                    }

                    this.creatingLeads = this.creatingLeads.filter((id) => id !== lead.linkedin_id);
                    this.createdFromFirstLeadFlag = true;
                }, 500);
            },
            error: (err) => {
                this.leadCreationErrorHandler(err, lead);
            }
        });
    }

    private leadCreationErrorHandler(err: any, lead: any) {
        this.creatingLeads = this.creatingLeads.filter((id) => id !== lead.linkedin_id);

        if (err?.status === 402 || err?.data?.statusCode === 402) {
            this.credits = 0;
            this.utilsService.showInfoToast(this.translate.instant('You\'re out of lead credits. Please upgrade or purchase a package.'));
        }
        else if (err?.data?.message) {
            this.utilsService.showErrorToast(err.data.message);
        }
        else {
            this.utilsService.showErrorToast(this.translate.instant('An error occurred while creating the lead.'));
        }
    }

    private chooseAccountDialog(lead: any, creationType: string): Observable<CreateResponse | null> {
        return from(this.dialogService.show({
            clickOutsideToClose: true,
            controller: 'SelectAccountDialogController',
            templateUrl: 'partials/selectaccountdialog.html',
            locals: {
                suggestedAccounts: lead.company.matching_accounts
            }
        })).pipe(
            switchMap((selectedAccount: any) => {
                const createPayload: LeadCreatePayload = {
                    linkedin_id: lead.linkedin_id,
                    type: creationType
                };

                if (selectedAccount) {
                    createPayload.account = selectedAccount.id;
                }

                if (creationType === 'contact_account_opportunity') {
                    return from(this.pipelinesService.get()).pipe(
                        switchMap((response) => this.choosePipelineDialog(createPayload, response)) // Chain pipeline dialog
                    ) as Observable<CreateResponse>;
                }

                this.creatingLeads.push(lead.linkedin_id);

                return this.leadsService.createFromLead(createPayload) as Observable<CreateResponse>;
            }),
            // eslint-disable-next-line handle-callback-err, @typescript-eslint/no-unused-vars
            catchError((err) => {
                return of(null);
            })
        );
    }

    private isTheSameCompany(companyA: any, companyB: any) {
        if (!companyA || !companyB) {
            return false;
        }

        return companyA.name === companyB.name && companyA.linkedin_id === companyB.linkedin_id;
    }

    getFindEmailTooltip(lead: any): string {
        const email = this.contactExistsAndHasEmail(lead);
        if (email) {
            return email;
        }

        if (!this.isFindEmailEnabled) {
            return this.translate.instant('Find email address is disabled.');
        }

        if (this.isFindEmailButtonDisabled(lead)) {
            return this.translate.instant('Company domain or name or lead name missing.');
        }

        if (this.attemptedEmailFind.includes(lead.linkedin_id)) {
            return this.translate.instant('No quality email address was found.');
        }

        return this.credits > 0 ? this.translate.instant('Search email address. Uses 1 lead credit.') : this.translate.instant('You\'re out of lead credits. Please upgrade or purchase a package.');
    }

    workflowButtonTooltipText(lead: any): string {
        if (this.isEmailButtonDisabled(lead)) {
            return this.translate.instant('Add an email address to the contact first.');
        }

        if (!this.contactExistsAndHasEmail(lead)) {
            return this.translate.instant('Search for an email address first.');
        }

        return this.translate.instant('Add to workflow');
    }

    private choosePipelineDialog(createPayload: LeadCreatePayload, pipelineInfo: any): ObservableInput<CreateResponse> {

        if (pipelineInfo.data.length > 1) {
            const choosePipelineDialog = this.matDialog.open(SfxDialog, {
                panelClass: 'sfx-dialog-container-sm',
                data: {
                    title: this.translate.instant('Choose Pipeline'),
                    confirm: this.translate.instant('Create Lead'),
                    theme: 'blue',
                    content: ChoosePipelineComponent,
                    context: {
                        availablePipelines: pipelineInfo.data
                    }
                }
            });

            return choosePipelineDialog.afterClosed()
                .pipe(
                    filter((context: DialogResponse<ChoosePipelineData>) => context?.confirmed),
                    mergeMap((context: DialogResponse<ChoosePipelineData>): ObservableInput<CreateResponse> => {

                        if (context.state.pipeline) {
                            createPayload.pipeline = context.state.pipeline.id;
                        }

                        this.creatingLeads.push(createPayload.linkedin_id);
                        return this.leadsService.createFromLead(createPayload) as ObservableInput<CreateResponse>;
                    }));
        }

        this.creatingLeads.push(createPayload.linkedin_id);
        return this.leadsService.createFromLead(createPayload) as ObservableInput<CreateResponse>;
    }

    createButtonTooltipText(lead: any): string {
        if (this.creatingLeads.includes(lead.linkedin_id)) {
            return this.translate.instant('Creating lead...');
        }

        return this.credits > 0 ? this.translate.instant('Uses 1 lead credit to find an email address.') : this.translate.instant('You\'re out of lead credits. Please upgrade or purchase a package.');
    }

    confirmCreateAction = (lead: any, createOption: PropValueUnion<typeof this.createOptions, 'name'>): void => {
        if (this.createdFromFirstLeadFlag === true) {
            return this.createAction(lead, createOption);
        }

        const confirmPurchaseDialog = this.dialogService.confirm({ multiple: true })
            .clickOutsideToClose(true)
            .escapeToClose(true)
            .title(this.translate.instant('Congrats on your first lead'))
            .htmlContent(`<div class="confirm-create-action-dialog">
                    ${this.translate.instant('Creating a contact from a lead may use 1 lead credit if we find a valid email address. If no quality email address is found, the lead is still created but no credit is used.')}
                </div>`)
            .ok(this.translate.instant('Got it'));

        return this.dialogService.show(confirmPurchaseDialog)
            .then(() => {

                this.createAction(lead, createOption);
            });
    }

    getCompleteAddress(address: any): string {
        if (!address) {
            return '';
        }

        const addressParts = [
            address.street,
            address.city,
            address.state,
            address.country
        ];

        return addressParts.filter((part) => !!part).join(', ');
    }

    getCompanyPicture(lead: any): string {
        if (lead.linked_account && lead.company.matching_accounts?.length) {
            return lead.company.matching_accounts[0].picture || lead.company.picture;
        }

        return lead.company.picture;
    }

    getCompanyName(lead: any): string {
        if (lead.linked_account && lead.company.matching_accounts?.length) {
            return lead.company.matching_accounts[0].name || lead.company.name;
        }

        return lead.company.name;
    }

    getLinkedInUrlFromSocialProfiles(socialProfiles: any): string {
        return socialProfiles.find((profile: any) => profile.type === 'linkedin')?.url || '';
    }

    getLeadsCountText(): string {

        // eslint-disable-next-line angular/definedundefined
        if (this.numberOfLeads === undefined) {
            return '';
        }

        const count = this.numberOfLeads >= 10000 ? '9999+' : this.numberOfLeads.toString();
        return count + ' ' + this.translate.instant('Leads');
    }
}
